import { useEffect, useState } from "react";
import { api, INR } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { TierBadge } from "@/components/TierBadge";
import { toast } from "sonner";
import { CheckCircle2, RotateCcw, Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Payouts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = user?.role === "admin" || user?.role === "dept_lead";
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [affs, setAffs] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const reload = () => api.get("/payouts", { params: { month } }).then((r) => setRows(r.data));
  useEffect(() => { reload(); }, [month]);
  useEffect(() => { api.get("/affiliates").then((r) => setAffs(r.data)); }, []);

  const affName = (id) => affs.find((a) => a.id === id)?.name || "—";
  const affTier = (id) => affs.find((a) => a.id === id)?.tier || "Base";

  const isLocked = rows.length > 0 && rows.every((r) => r._locked);
  const paidCount = rows.filter((r) => r.status === "paid").length;
  const pendingCount = rows.filter((r) => r.status === "pending").length;

  const totalCommission = rows.reduce((s, r) => s + (r.commission_amount || 0), 0);
  const totalRewardMrp = rows.reduce(
    (s, r) =>
      s +
      (r.monthly_reward_mrp || 0) +
      (r.fast_track_reward_mrp || 0) +
      (r.tier_unlock_reward_mrp || 0) +
      (r.referral_reward_mrp || 0),
    0
  );

  const markPaid = async (payoutId) => {
    setBusyId(payoutId);
    try {
      await api.post(`/payouts/${payoutId}/mark-paid`);
      toast.success("Marked as paid");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark paid");
    } finally {
      setBusyId(null);
    }
  };
  const undoPaid = async (payoutId) => {
    if (!window.confirm("Undo this payment? The row will go back to pending.")) return;
    setBusyId(payoutId);
    try {
      await api.post(`/payouts/${payoutId}/undo-paid`);
      toast.success("Payment undone");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not undo");
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = () => {
    const headers = [
      "Affiliate", "Tier", "Sales", "New Customers", "Commission %", "Commission ₹",
      "Monthly Reward MRP", "Fast Track MRP", "Tier Unlock MRP", "Referral Reward MRP", "Status",
    ];
    const lines = [headers.join(",")].concat(
      rows.map((r) =>
        [
          r.affiliate_name || affName(r.affiliate_id),
          r.tier || affTier(r.affiliate_id),
          r.sales_total,
          r.new_customers,
          r.commission_percent,
          r.commission_amount,
          r.monthly_reward_mrp,
          r.fast_track_reward_mrp,
          r.tier_unlock_reward_mrp || 0,
          r.referral_reward_mrp || 0,
          r.status || "preview",
        ].join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payouts-${month}.csv`;
    a.click();
  };

  return (
    <div>
      <PageHeader
        title="Payouts"
        subtitle={isLocked ? "Locked snapshot — imported from CSV, mark as paid below" : "Live preview — commission and rewards owed for the selected month"}
        actions={
          <div className="flex flex-wrap gap-2 items-center">
            <input
              data-testid="payout-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium"
            />
            <Button variant="outline" onClick={exportCsv} className="rounded-full h-10" data-testid="export-payouts">
              Export CSV
            </Button>
          </div>
        }
      />
      <div className="p-6 md:p-10">
        {isLocked && (
          <div data-testid="payouts-locked-banner" className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 px-5 py-3.5 flex items-center gap-3 text-sm">
            <Lock className="h-4 w-4 text-amber-600 shrink-0" />
            <div className="flex-1">
              <span className="font-semibold text-amber-700">Locked snapshot</span> — these values were manually imported and won&apos;t auto-update as new orders come in.
              {paidCount > 0 && <span className="ml-2 text-xs text-muted-foreground">· {paidCount} paid · {pendingCount} pending</span>}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="rounded-2xl border border-border/60 bg-card p-5">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Total Commission</div>
            <div className="text-2xl font-semibold mt-2" data-testid="payouts-total-commission">{INR(totalCommission)}</div>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card p-5">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Products Reward (MRP)</div>
            <div className="text-2xl font-semibold mt-2" data-testid="payouts-total-reward-mrp">{INR(totalRewardMrp)}</div>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card p-5">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Affiliates in Payout</div>
            <div className="text-2xl font-semibold mt-2" data-testid="payouts-affiliate-count">{rows.length}</div>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/40">
              <tr>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Affiliate</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Tier</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Sales</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">New</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">%</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Commission</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Reward MRP</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Fast Track</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Tier Unlock</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Referral</th>
                {isLocked && <th className="text-right text-[11px] font-semibold uppercase tracking-widest text-muted-foreground px-6 py-4">Status</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isPaid = r.status === "paid";
                return (
                  <tr key={r.id || r.affiliate_id} data-testid={`payout-row-${r.affiliate_id}`} className={`border-t border-border/50 ${isPaid ? "bg-emerald-500/5" : ""}`}>
                    <td className="px-6 py-3 text-sm font-medium">{r.affiliate_name || affName(r.affiliate_id)}</td>
                    <td className="px-6 py-3"><TierBadge tier={r.tier || affTier(r.affiliate_id)} /></td>
                    <td className="px-6 py-3 text-sm text-right">{INR(r.sales_total)}</td>
                    <td className="px-6 py-3 text-sm text-right">{r.new_customers}</td>
                    <td className="px-6 py-3 text-sm text-right">{r.commission_percent}%</td>
                    <td className="px-6 py-3 text-sm font-semibold text-right">{INR(r.commission_amount)}</td>
                    <td className="px-6 py-3 text-sm text-right">{r.monthly_reward_mrp ? INR(r.monthly_reward_mrp) : "—"}</td>
                    <td className="px-6 py-3 text-sm text-right">{r.fast_track_reward_mrp ? INR(r.fast_track_reward_mrp) : "—"}</td>
                    <td className="px-6 py-3 text-sm text-right">{r.tier_unlock_reward_mrp ? <span className="font-semibold text-primary">{INR(r.tier_unlock_reward_mrp)}</span> : "—"}</td>
                    <td className="px-6 py-3 text-sm text-right">{r.referral_reward_mrp ? <span className="font-semibold text-primary" data-testid={`payout-referral-${r.affiliate_id}`}>{INR(r.referral_reward_mrp)}</span> : "—"}</td>
                    {isLocked && (
                      <td className="px-6 py-3 text-right">
                        {isPaid ? (
                          <div className="flex items-center justify-end gap-2">
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full px-2 py-1 bg-emerald-500/10 text-emerald-600 font-semibold">
                              <CheckCircle2 className="h-3 w-3" /> Paid
                            </span>
                            {isAdmin && (
                              <button
                                data-testid={`undo-paid-${r.id}`}
                                disabled={busyId === r.id}
                                onClick={() => undoPaid(r.id)}
                                title="Undo payment (admin only)"
                                className="h-7 w-7 rounded-full bg-secondary hover:bg-secondary/70 flex items-center justify-center disabled:opacity-40"
                              >
                                <RotateCcw className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        ) : canManage ? (
                          <Button
                            data-testid={`mark-paid-${r.id}`}
                            disabled={busyId === r.id}
                            onClick={() => markPaid(r.id)}
                            size="sm"
                            className="rounded-full h-8 text-xs"
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" /> {busyId === r.id ? "…" : "Mark Paid"}
                          </Button>
                        ) : (
                          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Pending</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={isLocked ? 11 : 10} className="px-6 py-12 text-center text-sm text-muted-foreground">
                    No payouts for {month}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
